import React, { useState } from 'react';
import { Control, Controller, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RefreshCw, CheckCircle, AlertCircle, X } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Switch } from './Switch';
import { Badge } from './Badge';
import { Spinner } from './Spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { toast } from 'sonner';
import styles from './UserSettingsAiFormLocalLlm.module.css';

// We need to define the FormValues type here or import it if it was exported.
// Since we are decoupling, we can define a subset or interface.
// Ideally, the parent should export its type, but to avoid circular deps if not careful,
// we'll define the shape we expect.
interface LocalLlmFormValues {
  localLlmEnabled: boolean;
  localLlmEndpoint: string | undefined;
  localLlmApiKey: string | undefined;
  localLlmModelsString: string | undefined;
  localLlmDefaultModel: string | undefined;
}

interface Props {
  control: Control<any>; // Using any to be flexible with the parent form's full schema
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
}

interface OllamaModel {
  name: string;
  modified_at: string;
  size: number;
}

interface OllamaTagsResponse {
  models: OllamaModel[];
}

export const UserSettingsAiFormLocalLlm = ({ control, setValue, watch }: Props) => {
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'none' | 'success' | 'error'>('none');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [detectedModels, setDetectedModels] = useState<string[]>([]);

  // Watch values to react to changes
  const endpoint = watch('localLlmEndpoint');
  const modelsString = watch('localLlmModelsString');
  const enabled = watch('localLlmEnabled');

  // Parse current models from the string
  const currentModels = modelsString
    ? modelsString
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
    : [];

  const handleTestConnection = async () => {
    if (!endpoint) {
      toast.error('Please enter an endpoint URL first');
      return;
    }

    setIsTesting(true);
    setConnectionStatus('none');
    setErrorMessage(null);

    try {
      const baseUrl = endpoint.replace(/\/$/, '');
      const apiKey = watch('localLlmApiKey');
      const headers: Record<string, string> = {};
      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      let models: string[] = [];

      // Try Ollama /api/tags first
      try {
        const res = await fetch(`${baseUrl}/api/tags`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data?.models && Array.isArray(data.models)) {
            models = data.models.map((m: any) => m.name);
          }
        }
      } catch { /* try next */ }

      // Fallback: /v1/models (OpenAI-compatible or proxy returning Ollama format)
      if (models.length === 0) {
        try {
          const res = await fetch(`${baseUrl}/v1/models`, { headers });
          if (res.ok) {
            const data = await res.json();
            if (data?.data && Array.isArray(data.data)) {
              models = data.data.map((m: any) => m.id);
            } else if (data?.models && Array.isArray(data.models)) {
              models = data.models.map((m: any) => m.name);
            }
          }
        } catch { /* try next */ }
      }

      if (models.length === 0) {
        throw new Error('Could not fetch models. Tried /api/tags and /v1/models.');
      }

      setDetectedModels(models);
      setValue('localLlmModelsString', models.join(', '), { shouldDirty: true });

      const currentDefault = watch('localLlmDefaultModel');
      if (!currentDefault || !models.includes(currentDefault)) {
        if (models.length > 0) {
          setValue('localLlmDefaultModel', models[0], { shouldDirty: true });
        }
      }

      setConnectionStatus('success');
      toast.success(`Found ${models.length} models`);
    } catch (err) {
      console.error('Connection failed', err);
      setConnectionStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Unknown error occurred');
      toast.error('Connection failed. Check console for details.');
    } finally {
      setIsTesting(false);
    }
  };

  const removeModel = (modelToRemove: string) => {
    const newModels = currentModels.filter((m: string) => m !== modelToRemove);
    setValue('localLlmModelsString', newModels.join(', '), { shouldDirty: true });

    // If we removed the default model, reset it
    if (watch('localLlmDefaultModel') === modelToRemove) {
      setValue('localLlmDefaultModel', '', { shouldDirty: true });
    }
  };

  const addModel = (modelToAdd: string) => {
    if (!currentModels.includes(modelToAdd)) {
      const newModels = [...currentModels, modelToAdd];
      setValue('localLlmModelsString', newModels.join(', '), { shouldDirty: true });
    }
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>Local LLM (Reverse Proxy)</h3>
        <Controller
          control={control}
          name="localLlmEnabled"
          render={({ field }) => (
            <div className={styles.switchWrapper}>
              <Switch
                id="local-llm-switch"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <label htmlFor="local-llm-switch" className={styles.switchLabel}>
                {field.value ? 'Enabled' : 'Disabled'}
              </label>
            </div>
          )}
        />
      </div>

      {enabled && (
        <div className={styles.fieldsGrid}>
          <div className={styles.fieldGroupFull}>
            <label className={styles.label}>Endpoint URL</label>
            <div className={styles.endpointInputGroup}>
              <Input
                {...control.register('localLlmEndpoint')}
                placeholder="http://localhost:11434"
                className={styles.endpointInput}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleTestConnection}
                disabled={isTesting || !endpoint}
                className={styles.testButton}
              >
                {isTesting ? <Spinner size="sm" /> : <RefreshCw size={16} />}
                Test & Fetch
              </Button>
            </div>
            {connectionStatus === 'success' && (
              <p className={styles.statusSuccess}>
                <CheckCircle size={14} /> Connection successful
              </p>
            )}
            {connectionStatus === 'error' && (
              <p className={styles.statusError}>
                <AlertCircle size={14} /> {errorMessage}
              </p>
            )}
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>API Key (Optional)</label>
            <Input type="password" {...control.register('localLlmApiKey')} placeholder="sk-..." />
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.label}>Default Model</label>
            <Controller
              control={control}
              name="localLlmDefaultModel"
              render={({ field }) => (
                <Select
                  value={field.value || '__empty'}
                  onValueChange={(val) => field.onChange(val === '__empty' ? '' : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentModels.length === 0 ? (
                      <SelectItem value="__empty" disabled>
                        No models listed
                      </SelectItem>
                    ) : (
                      currentModels.map((model: string) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className={styles.fieldGroupFull}>
            <label className={styles.label}>Available Models</label>

            {/* Chips Display */}
            <div className={styles.chipsContainer}>
              {currentModels.length > 0 ? (
                currentModels.map((model: string) => (
                  <Badge key={model} variant="secondary" className={styles.modelChip}>
                    {model}
                    <button
                      type="button"
                      onClick={() => removeModel(model)}
                      className={styles.removeChipBtn}
                      aria-label={`Remove ${model}`}
                    >
                      <X size={12} />
                    </button>
                  </Badge>
                ))
              ) : (
                <span className={styles.emptyText}>
                  No models configured. Fetch or add manually below.
                </span>
              )}
            </div>

            {/* Manual Entry Fallback */}
            <div className={styles.manualEntry}>
              <label className={styles.subLabel}>Manual Edit (Comma separated)</label>
              <Input
                {...control.register('localLlmModelsString')}
                placeholder="llama3, mistral, codellama"
              />
            </div>

            {/* Detected but not added models (if any logic needed later, but right now fetch adds all) */}
          </div>
        </div>
      )}
    </section>
  );
};
